import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import {
  Clock, Users, Eye, Globe, TrendingUp, RefreshCw,
  Smartphone, Monitor, MousePointer, ArrowUpRight,
  MessageSquare, ChevronRight, Activity, Repeat2,
} from 'lucide-react';
import { useWhenconnectStats } from './useWhenconnectStats';
import { webvonkClient } from '../lib/supabase';

const GREEN  = '#22c55e';
const PURPLE = '#A020C6';
const ACCENT = GREEN;

// ─── Animations ──────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ─── Helpers ─────────────────────────────────────────────────
function getIsLight() {
  const saved = localStorage.getItem('whenconnect_theme');
  if (saved) return saved === 'light';
  return window.matchMedia('(prefers-color-scheme: light)').matches;
}
function getGreeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Goedemorgen' : h < 18 ? 'Goedemiddag' : 'Goedenavond';
}
function getTime() {
  return new Date().toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
}
function getDate() {
  return new Date().toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
}
function fmt(n) {
  if (n === undefined || n === null) return '—';
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}
function fmtDuration(sec) {
  if (!sec) return '—';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}
function fmtTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const diffMin = Math.round((now - d) / 60000);
  if (diffMin < 1) return 'zojuist';
  if (diffMin < 60) return `${diffMin} min geleden`;
  if (diffMin < 1440) return `${Math.floor(diffMin / 60)} uur geleden`;
  return d.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}
function friendlyPage(page) {
  if (!page || page === '/') return 'Homepagina';
  if (page.includes('/#over'))      return 'Over mij';
  if (page.includes('/#diensten'))  return 'Diensten';
  if (page.includes('/#contact'))   return 'Contact';
  if (page.includes('/#werkwijze')) return 'Werkwijze';
  if (page.includes('cookie'))      return 'Cookieverklaring';
  return page;
}
function pctBar(pct, color = ACCENT) {
  return (
    <BarWrap>
      <BarFill style={{ width: `${Math.min(pct, 100)}%`, background: color }} />
    </BarWrap>
  );
}
function deviceIcon(device) {
  if (device === 'Mobiel') return <Smartphone size={12} />;
  return <Monitor size={12} />;
}

// ─── Styled ──────────────────────────────────────────────────
const Wrap = styled.div`
  height: 100%;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: ${p => p.$light ? '#f5f5f7' : '#0a0a0a'};
`;
const Page = styled.div`
  padding: 40px 40px 100px;
  max-width: 920px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 32px;
  @media (max-width: 600px) { padding: 28px 16px 80px; gap: 24px; }
`;
const Anim = styled.div`
  animation: ${fadeUp} 0.3s ease both;
  animation-delay: ${p => p.$d || '0ms'};
`;
const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;
const GreetingTitle = styled.h1`
  font-size: 1.4rem;
  font-weight: 700;
  color: ${p => p.$light ? '#111' : '#fff'};
  margin: 0 0 2px;
  letter-spacing: -0.02em;
`;
const GreetingSub = styled.p`
  font-size: 0.8rem;
  color: ${p => p.$light ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)'};
  margin: 0;
`;
const TimeTag = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  background: ${p => p.$light ? '#fff' : 'rgba(255,255,255,0.06)'};
  border: 1px solid ${p => p.$light ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.08)'};
  border-radius: 10px;
  padding: 6px 12px;
  font-size: 0.75rem;
  font-weight: 500;
  color: ${p => p.$light ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.4)'};
  white-space: nowrap;
  flex-shrink: 0;
`;
const DateSpan = styled.span`
  @media (max-width: 480px) { display: none; }
`;
const SectionLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${p => p.$light ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.3)'};
  margin-bottom: 12px;
`;
const TabRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;
const Tabs = styled.div`
  display: flex;
  background: ${p => p.$light ? '#fff' : 'rgba(255,255,255,0.06)'};
  border: 1px solid ${p => p.$light ? 'rgba(0,0,0,0.10)' : 'rgba(255,255,255,0.08)'};
  border-radius: 10px;
  padding: 3px;
  gap: 2px;
`;
const Tab = styled.button`
  font-size: 0.75rem;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 7px;
  border: none;
  cursor: pointer;
  font-family: inherit;
  background: ${p => p.$active ? (p.$light ? '#f5f5f7' : 'rgba(255,255,255,0.12)') : 'transparent'};
  color: ${p => p.$active ? (p.$light ? '#111' : '#fff') : (p.$light ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)')};
  box-shadow: ${p => p.$active ? '0 1px 3px rgba(0,0,0,0.10)' : 'none'};
  transition: all 0.15s;
  &:hover { color: ${p => p.$light ? '#111' : '#fff'}; }
`;
const RefreshBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${p => p.$light ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.35)'};
  padding: 4px;
  display: flex;
  align-items: center;
  border-radius: 8px;
  transition: all 0.15s;
  &:hover { background: ${p => p.$light ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)'}; color: ${p => p.$light ? '#111' : '#fff'}; }
`;

// Stat cards
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  @media (max-width: 700px) { grid-template-columns: repeat(2, 1fr); }
`;
const StatCard = styled.div`
  background: ${p => p.$light ? '#fff' : 'rgba(255,255,255,0.05)'};
  border: 1px solid ${p => p.$light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.07)'};
  border-radius: 14px;
  padding: 16px;
`;
const StatIcon = styled.div`
  width: 26px; height: 26px;
  border-radius: 7px;
  background: ${p => p.$color || ACCENT}18;
  color: ${p => p.$color || ACCENT};
  display: flex; align-items: center; justify-content: center;
  margin-bottom: 10px;
`;
const StatValue = styled.div`
  font-size: 1.7rem;
  font-weight: 800;
  color: ${p => p.$light ? '#111' : '#fff'};
  letter-spacing: -0.04em;
  line-height: 1;
  margin-bottom: 3px;
`;
const StatLabel = styled.div`
  font-size: 0.7rem;
  color: ${p => p.$light ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)'};
  font-weight: 500;
`;
const StatSub = styled.div`
  font-size: 0.67rem;
  color: ${p => p.$light ? 'rgba(0,0,0,0.28)' : 'rgba(255,255,255,0.28)'};
  margin-top: 2px;
`;

// Card
const Card = styled.div`
  background: ${p => p.$light ? '#fff' : 'rgba(255,255,255,0.05)'};
  border: 1px solid ${p => p.$light ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.07)'};
  border-radius: 14px;
  overflow: hidden;
`;
const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 12px;
  border-bottom: 1px solid ${p => p.$light ? 'rgba(0,0,0,0.07)' : 'rgba(255,255,255,0.07)'};
`;
const CardTitle = styled.div`
  font-size: 0.82rem;
  font-weight: 600;
  color: ${p => p.$light ? '#111' : '#fff'};
  display: flex; align-items: center; gap: 6px;
`;
const CardSub = styled.div`
  font-size: 0.67rem;
  color: ${p => p.$light ? 'rgba(0,0,0,0.33)' : 'rgba(255,255,255,0.28)'};
  margin-top: 2px;
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 16px;
  border-bottom: 1px solid ${p => p.$light ? 'rgba(0,0,0,0.055)' : 'rgba(255,255,255,0.05)'};
  gap: 10px;
  &:last-child { border-bottom: none; }
`;
const RowName = styled.div`
  font-size: 0.8rem;
  color: ${p => p.$light ? '#111' : '#fff'};
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex; align-items: center; gap: 6px;
`;
const RowVal = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${p => p.$light ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)'};
  flex-shrink: 0;
`;
const RowExtra = styled.div`
  font-size: 0.67rem;
  color: ${p => p.$light ? 'rgba(0,0,0,0.28)' : 'rgba(255,255,255,0.28)'};
`;
const Empty = styled.div`
  padding: 20px 16px;
  font-size: 0.78rem;
  color: ${p => p.$light ? 'rgba(0,0,0,0.3)' : 'rgba(255,255,255,0.25)'};
`;
const BarWrap = styled.div`
  width: 60px;
  height: 4px;
  border-radius: 2px;
  background: rgba(128,128,128,0.15);
  overflow: hidden;
  flex-shrink: 0;
`;
const BarFill = styled.div`
  height: 100%;
  border-radius: 2px;
  transition: width 0.5s ease;
`;
const TwoCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;
const ThreeCol = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 12px;
  @media (max-width: 700px) { grid-template-columns: 1fr 1fr; }
  @media (max-width: 440px) { grid-template-columns: 1fr; }
`;
const Badge = styled.span`
  font-size: 0.62rem;
  font-weight: 600;
  padding: 2px 6px;
  border-radius: 20px;
  background: ${p => p.$color || ACCENT}20;
  color: ${p => p.$color || ACCENT};
  flex-shrink: 0;
`;
const ConvBlock = styled.div`
  display: flex;
  gap: 10px;
  padding: 14px 16px;
`;
const ConvItem = styled.div`
  flex: 1;
  background: ${p => p.$light ? '#f9f9fb' : 'rgba(255,255,255,0.04)'};
  border-radius: 10px;
  padding: 12px;
  text-align: center;
`;
const ConvValue = styled.div`
  font-size: 1.5rem;
  font-weight: 800;
  color: ${p => p.$color || (p.$light ? '#111' : '#fff')};
  letter-spacing: -0.03em;
`;
const ConvLabel = styled.div`
  font-size: 0.67rem;
  color: ${p => p.$light ? 'rgba(0,0,0,0.38)' : 'rgba(255,255,255,0.35)'};
  margin-top: 2px;
`;
const ScrollMilestoneRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 16px;
  border-bottom: 1px solid ${p => p.$light ? 'rgba(0,0,0,0.055)' : 'rgba(255,255,255,0.05)'};
  &:last-child { border-bottom: none; }
`;
const MilestoneLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: ${p => p.$light ? '#111' : '#fff'};
  width: 36px;
  flex-shrink: 0;
`;
const MilestoneBar = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: rgba(128,128,128,0.12);
  overflow: hidden;
`;
const MilestoneFill = styled.div`
  height: 100%;
  border-radius: 3px;
  background: ${p => p.$color};
  transition: width 0.6s ease;
`;
const MilestoneVal = styled.div`
  font-size: 0.72rem;
  color: ${p => p.$light ? 'rgba(0,0,0,0.38)' : 'rgba(255,255,255,0.35)'};
  width: 28px;
  text-align: right;
  flex-shrink: 0;
`;
const RecentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid ${p => p.$light ? 'rgba(0,0,0,0.055)' : 'rgba(255,255,255,0.05)'};
  &:last-child { border-bottom: none; }
`;
const RecentMain = styled.div`
  flex: 1;
  min-width: 0;
`;
const RecentDevice = styled.div`
  font-size: 0.78rem;
  color: ${p => p.$light ? '#111' : '#fff'};
  display: flex; align-items: center; gap: 5px;
`;
const RecentMeta = styled.div`
  font-size: 0.67rem;
  color: ${p => p.$light ? 'rgba(0,0,0,0.35)' : 'rgba(255,255,255,0.3)'};
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const RecentRight = styled.div`
  text-align: right;
  flex-shrink: 0;
`;
const LastUpdated = styled.div`
  font-size: 0.67rem;
  color: ${p => p.$light ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.2)'};
  text-align: right;
  padding-top: 4px;
`;

const ContactBanner = styled.a`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${p => p.$light ? '#fff' : 'rgba(249,115,22,0.10)'};
  border: 1px solid ${p => p.$light ? 'rgba(249,115,22,0.35)' : 'rgba(249,115,22,0.30)'};
  border-radius: 14px;
  padding: 14px 18px;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: ${p => p.$light ? 'rgba(249,115,22,0.06)' : 'rgba(249,115,22,0.16)'}; }
`;
const ContactBannerDot = styled.div`
  width: 36px; height: 36px;
  border-radius: 50%;
  background: rgba(249,115,22,0.15);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  color: #f97316;
`;
const ContactBannerText = styled.div`
  flex: 1;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${p => p.$light ? '#111' : '#fff'};
`;
const ContactBannerSub = styled.div`
  font-size: 0.72rem;
  font-weight: 400;
  color: ${p => p.$light ? 'rgba(0,0,0,0.4)' : 'rgba(255,255,255,0.4)'};
  margin-top: 1px;
`;
const ContactBannerBadge = styled.div`
  background: #f97316;
  color: #fff;
  font-size: 0.75rem;
  font-weight: 700;
  border-radius: 50px;
  padding: 2px 10px;
  flex-shrink: 0;
`;

const PERIODS = { today: 'Vandaag', week: 'Week', month: 'Maand', all: 'Alles' };

// ─── Component ───────────────────────────────────────────────
export default function WhenconnectDashboard() {
  const [isLight, setIsLight] = useState(getIsLight);
  const [time, setTime] = useState(getTime());
  const [period, setPeriod] = useState('week');
  const [newContacts, setNewContacts] = useState(0);
  const { data: d, loading, lastFetched, reload } = useWhenconnectStats(period);

  useEffect(() => {
    webvonkClient
      .from('wc_contacten')
      .select('id', { count: 'exact', head: true })
      .or('status.eq.Nieuw,status.is.null')
      .then(({ count }) => setNewContacts(count ?? 0));
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTime(getTime()), 10000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: light)');
    const onMq = (e) => { if (!localStorage.getItem('whenconnect_theme')) setIsLight(e.matches); };
    const onStorage = () => setIsLight(getIsLight());
    mq.addEventListener('change', onMq);
    window.addEventListener('storage', onStorage);
    return () => { mq.removeEventListener('change', onMq); window.removeEventListener('storage', onStorage); };
  }, []);

  const L = isLight;

  // Scroll milestone kleur gradient: rood→oranje→groen
  function milestoneColor(pct) {
    if (pct <= 25) return '#22c55e';
    if (pct <= 50) return '#84cc16';
    if (pct <= 75) return '#f59e0b';
    if (pct <= 90) return '#f97316';
    return '#ef4444';
  }

  const maxMilestone = d ? Math.max(...d.scrollMilestones.map(m => m.count), 1) : 1;
  const maxSource    = d ? Math.max(...d.topSources.map(s => s.count), 1) : 1;
  const maxDevice    = d ? Math.max(...(d.deviceMap || []).map(x => x.count), 1) : 1;
  const maxBrowser   = d ? Math.max(...(d.browserMap || []).map(x => x.count), 1) : 1;

  return (
    <Wrap $light={L}>
      <Page>

        {/* Header */}
        <Anim $d="0ms">
          <HeaderRow>
            <div>
              <GreetingTitle $light={L}>{getGreeting()}, Wendy</GreetingTitle>
              <GreetingSub $light={L}>Hier zijn de statistieken van Whenconnect</GreetingSub>
            </div>
            <TimeTag $light={L}>
              <Clock size={11} />
              {time}<DateSpan> · {getDate()}</DateSpan>
            </TimeTag>
          </HeaderRow>
        </Anim>

        {/* Nieuwe contactinzendingen */}
        {newContacts > 0 && (
          <Anim $d="20ms">
            <ContactBanner href="/Whenconnect/contactinzendingen" $light={L}>
              <ContactBannerDot><MessageSquare size={16} /></ContactBannerDot>
              <div style={{ flex: 1 }}>
                <ContactBannerText $light={L}>Nieuwe contactinzendingen</ContactBannerText>
                <ContactBannerSub $light={L}>Klik om te bekijken en te beantwoorden</ContactBannerSub>
              </div>
              <ContactBannerBadge>{newContacts}</ContactBannerBadge>
            </ContactBanner>
          </Anim>
        )}

        {/* Periode tabs */}
        <Anim $d="40ms">
          <TabRow>
            <Tabs $light={L}>
              {Object.entries(PERIODS).map(([key, label]) => (
                <Tab key={key} $active={period === key} $light={L} onClick={() => setPeriod(key)}>
                  {label}
                </Tab>
              ))}
            </Tabs>
            <RefreshBtn $light={L} onClick={reload} title="Verversen">
              <RefreshCw size={13} />
            </RefreshBtn>
          </TabRow>

          {/* Hoofdcijfers */}
          <StatsGrid>
            <StatCard $light={L}>
              <StatIcon $color={GREEN}><Users size={13} /></StatIcon>
              <StatValue $light={L}>{loading ? '…' : fmt(d?.uniqueVis)}</StatValue>
              <StatLabel $light={L}>Unieke bezoekers</StatLabel>
              <StatSub $light={L}>{loading ? '' : `${fmt(d?.newVis)} nieuw · ${fmt(d?.returning)} terug`}</StatSub>
            </StatCard>
            <StatCard $light={L}>
              <StatIcon $color={PURPLE}><Activity size={13} /></StatIcon>
              <StatValue $light={L}>{loading ? '…' : fmt(d?.total)}</StatValue>
              <StatLabel $light={L}>Sessies</StatLabel>
              <StatSub $light={L}>{loading ? '' : `${fmt(d?.avgPages)} pagina's gem.`}</StatSub>
            </StatCard>
            <StatCard $light={L}>
              <StatIcon $color="#3b82f6"><Eye size={13} /></StatIcon>
              <StatValue $light={L}>{loading ? '…' : fmt(d?.totalPageviews)}</StatValue>
              <StatLabel $light={L}>Paginaweergaven</StatLabel>
              <StatSub $light={L}>{loading ? '' : `${d?.avgScroll ?? 0}% gem. scroll`}</StatSub>
            </StatCard>
            <StatCard $light={L}>
              <StatIcon $color="#f59e0b"><Clock size={13} /></StatIcon>
              <StatValue $light={L} style={{ fontSize: '1.3rem' }}>{loading ? '…' : fmtDuration(d?.avgDuration)}</StatValue>
              <StatLabel $light={L}>Gem. sessieduur</StatLabel>
              <StatSub $light={L}>{loading ? '' : `${d?.bounceRate ?? 0}% bouncepercentage`}</StatSub>
            </StatCard>
          </StatsGrid>
        </Anim>

        {/* Gedrag & Conversie */}
        <Anim $d="80ms">
          <SectionLabel $light={L}>Gedrag & conversie</SectionLabel>
          <TwoCol>
            {/* CTA + formulier */}
            <Card $light={L}>
              <CardHeader $light={L}>
                <div>
                  <CardTitle $light={L}><MousePointer size={13} /> Acties bezoekers</CardTitle>
                  <CardSub $light={L}>Kliks op knoppen en formulieren</CardSub>
                </div>
              </CardHeader>
              <ConvBlock $light={L}>
                <ConvItem $light={L}>
                  <ConvValue $color={GREEN} $light={L}>{loading ? '…' : fmt(d?.ctaClicks)}</ConvValue>
                  <ConvLabel $light={L}>Kennismaken geklikt</ConvLabel>
                </ConvItem>
                <ConvItem $light={L}>
                  <ConvValue $color="#3b82f6" $light={L}>{loading ? '…' : fmt(d?.formStarts)}</ConvValue>
                  <ConvLabel $light={L}>Formulier gestart</ConvLabel>
                </ConvItem>
                <ConvItem $light={L}>
                  <ConvValue $color={d?.formConv > 50 ? GREEN : '#f59e0b'} $light={L}>{loading ? '…' : `${d?.formConv ?? 0}%`}</ConvValue>
                  <ConvLabel $light={L}>Formulier ingevuld</ConvLabel>
                </ConvItem>
              </ConvBlock>
              {d?.topButtons?.length > 0 && (
                <>
                  {d.topButtons.map((btn, i) => (
                    <Row key={i} $light={L}>
                      <RowName $light={L}>{btn.text}</RowName>
                      <RowVal $light={L}>{btn.count}×</RowVal>
                    </Row>
                  ))}
                </>
              )}
            </Card>

            {/* Scroll depth */}
            <Card $light={L}>
              <CardHeader $light={L}>
                <div>
                  <CardTitle $light={L}><TrendingUp size={13} /> Hoe ver scrollen bezoekers?</CardTitle>
                  <CardSub $light={L}>Percentage bezoekers dat dat punt bereikt</CardSub>
                </div>
              </CardHeader>
              {loading
                ? <Empty $light={L}>Laden…</Empty>
                : d?.scrollMilestones.map(({ pct, count, percentage }) => (
                  <ScrollMilestoneRow key={pct} $light={L}>
                    <MilestoneLabel $light={L}>{pct}%</MilestoneLabel>
                    <MilestoneBar>
                      <MilestoneFill
                        $color={milestoneColor(pct)}
                        style={{ width: `${percentage}%` }}
                      />
                    </MilestoneBar>
                    <MilestoneVal $light={L}>{percentage}%</MilestoneVal>
                  </ScrollMilestoneRow>
                ))
              }
            </Card>
          </TwoCol>
        </Anim>

        {/* Top pagina's */}
        <Anim $d="120ms">
          <SectionLabel $light={L}>Pagina's</SectionLabel>
          <Card $light={L}>
            <CardHeader $light={L}>
              <div>
                <CardTitle $light={L}><Eye size={13} /> Meest bekeken pagina's</CardTitle>
                <CardSub $light={L}>Met gemiddelde scroll en tijd</CardSub>
              </div>
            </CardHeader>
            {loading
              ? <Empty $light={L}>Laden…</Empty>
              : !d?.topPages?.length
              ? <Empty $light={L}>Nog geen data</Empty>
              : d.topPages.map((p, i) => (
                <Row key={i} $light={L}>
                  <RowName $light={L}>
                    <ChevronRight size={11} style={{ opacity: 0.4 }} />
                    {friendlyPage(p.page)}
                  </RowName>
                  <RowExtra $light={L}>{p.avgScroll}% scroll</RowExtra>
                  <RowExtra $light={L}>{fmtDuration(p.avgTime)}</RowExtra>
                  <RowVal $light={L}>{p.views}×</RowVal>
                </Row>
              ))
            }
          </Card>
        </Anim>

        {/* Verkeersbronnen */}
        <Anim $d="160ms">
          <SectionLabel $light={L}>Hoe vinden ze de site?</SectionLabel>
          <TwoCol>
            <Card $light={L}>
              <CardHeader $light={L}>
                <div>
                  <CardTitle $light={L}><ArrowUpRight size={13} /> Verkeersbronnen</CardTitle>
                  <CardSub $light={L}>Waar komen bezoekers vandaan</CardSub>
                </div>
              </CardHeader>
              {loading
                ? <Empty $light={L}>Laden…</Empty>
                : !d?.topSources?.length
                ? <Empty $light={L}>Nog geen data</Empty>
                : d.topSources.map((s, i) => (
                  <Row key={i} $light={L}>
                    <RowName $light={L}>{s.source === 'Direct' ? '🔗 Direct' : `🌐 ${s.source}`}</RowName>
                    {pctBar(Math.round((s.count / maxSource) * 100))}
                    <RowVal $light={L}>{s.count}</RowVal>
                  </Row>
                ))
              }
            </Card>

            {d?.topUtm?.length > 0 && (
              <Card $light={L}>
                <CardHeader $light={L}>
                  <div>
                    <CardTitle $light={L}><TrendingUp size={13} /> Campagnes (UTM)</CardTitle>
                    <CardSub $light={L}>Betaald verkeer en campagnes</CardSub>
                  </div>
                </CardHeader>
                {d.topUtm.map((u, i) => (
                  <Row key={i} $light={L}>
                    <RowName $light={L}>{u.campaign}</RowName>
                    <RowVal $light={L}>{u.count} sessies</RowVal>
                  </Row>
                ))}
              </Card>
            )}
          </TwoCol>
        </Anim>

        {/* Apparaten & browsers */}
        <Anim $d="200ms">
          <SectionLabel $light={L}>Apparaten & browsers</SectionLabel>
          <ThreeCol>
            <Card $light={L}>
              <CardHeader $light={L}>
                <div>
                  <CardTitle $light={L}><Smartphone size={13} /> Apparaat</CardTitle>
                </div>
              </CardHeader>
              {loading
                ? <Empty $light={L}>Laden…</Empty>
                : (d?.deviceMap || []).map((x, i) => (
                  <Row key={i} $light={L}>
                    <RowName $light={L}>{deviceIcon(x.name)} {x.name}</RowName>
                    {pctBar(Math.round((x.count / maxDevice) * 100), PURPLE)}
                    <RowVal $light={L}>{x.count}</RowVal>
                  </Row>
                ))
              }
            </Card>

            <Card $light={L}>
              <CardHeader $light={L}>
                <div>
                  <CardTitle $light={L}><Globe size={13} /> Browser</CardTitle>
                </div>
              </CardHeader>
              {loading
                ? <Empty $light={L}>Laden…</Empty>
                : (d?.browserMap || []).map((x, i) => (
                  <Row key={i} $light={L}>
                    <RowName $light={L}>{x.name}</RowName>
                    {pctBar(Math.round((x.count / maxBrowser) * 100), '#3b82f6')}
                    <RowVal $light={L}>{x.count}</RowVal>
                  </Row>
                ))
              }
            </Card>

            <Card $light={L}>
              <CardHeader $light={L}>
                <div>
                  <CardTitle $light={L}><Monitor size={13} /> Besturingssysteem</CardTitle>
                </div>
              </CardHeader>
              {loading
                ? <Empty $light={L}>Laden…</Empty>
                : (d?.osMap || []).map((x, i) => (
                  <Row key={i} $light={L}>
                    <RowName $light={L}>{x.name}</RowName>
                    <RowVal $light={L}>{x.count}</RowVal>
                  </Row>
                ))
              }
            </Card>
          </ThreeCol>
        </Anim>

        {/* Recente sessies */}
        <Anim $d="240ms">
          <SectionLabel $light={L}>Recente bezoekers</SectionLabel>
          <Card $light={L}>
            <CardHeader $light={L}>
              <div>
                <CardTitle $light={L}><Repeat2 size={13} /> Laatste sessies</CardTitle>
                <CardSub $light={L}>Meest recente bezoeken (excl. intern)</CardSub>
              </div>
            </CardHeader>
            {loading
              ? <Empty $light={L}>Laden…</Empty>
              : !d?.recentSessions?.length
              ? <Empty $light={L}>Nog geen bezoekers</Empty>
              : d.recentSessions.map((s, i) => (
                <RecentRow key={i} $light={L}>
                  <RecentMain>
                    <RecentDevice $light={L}>
                      {deviceIcon(s.device)}
                      {s.browser} · {s.os}
                      {s.isReturning && <Badge $color={GREEN}>terugkerend</Badge>}
                      {s.isInternal && <Badge $color="#f59e0b">intern</Badge>}
                    </RecentDevice>
                    <RecentMeta $light={L}>
                      {s.source} · {friendlyPage(s.landing)} · {s.pages ?? 0} pagina{(s.pages ?? 0) !== 1 ? "'s" : ''}
                    </RecentMeta>
                  </RecentMain>
                  <RecentRight>
                    <RowVal $light={L}>{fmtDuration(s.duration)}</RowVal>
                    <RecentMeta $light={L}>{fmtTime(s.createdAt)}</RecentMeta>
                  </RecentRight>
                </RecentRow>
              ))
            }
          </Card>
        </Anim>

        {lastFetched && (
          <LastUpdated $light={L}>
            Bijgewerkt om {lastFetched.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </LastUpdated>
        )}

      </Page>
    </Wrap>
  );
}
